<template>
  <div
    v-if="mapHostMounted"
    ref="mapShell"
    class="flex flex-col overflow-hidden bg-white"
    :class="
      active
        ? 'mt-4 h-[70vh] min-h-[480px] rounded-lg border border-gray-200 shadow-sm sm:h-[650px] sm:min-h-[600px]'
        : 'fixed left-[-10000px] top-0 -z-10 h-[700px] w-[800px] opacity-0 pointer-events-none'
    "
    :aria-hidden="!active"
    aria-labelledby="donation-map-title"
  >
    <div
      class="flex shrink-0 flex-col gap-3 border-b border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:gap-3 sm:px-6"
    >
      <h2
        id="donation-map-title"
        class="min-w-0 text-base font-semibold text-gray-900 sm:flex-1 sm:text-lg"
      >
        Donation Locations
      </h2>
      <div
        v-if="active && canDrawExportArea"
        class="flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end"
      >
        <template v-if="isDrawing">
          <span class="text-xs leading-snug text-gray-600">
            Click map to add points ({{ draftPointCount }})
          </span>
          <div class="flex flex-wrap items-center gap-2">
            <BaseButton
              variant="primary"
              size="sm"
              :disabled="draftPointCount < 3"
              @click="finishDrawPolygon"
            >
              Finish Area
            </BaseButton>
            <BaseButton variant="ghost" size="sm" @click="cancelDrawPolygon"> Cancel </BaseButton>
          </div>
        </template>
        <template v-else>
          <div class="flex flex-wrap items-center gap-2">
            <BaseButton
              v-if="!hasActivePolygon"
              variant="secondary"
              size="sm"
              :disabled="!mapReady"
              @click="startDrawPolygon"
            >
              Draw Area
            </BaseButton>
            <BaseButton
              v-if="hasActivePolygon"
              variant="ghost"
              size="sm"
              @click="clearDrawnPolygon"
            >
              Clear Area
            </BaseButton>
          </div>
        </template>
      </div>
    </div>

    <div class="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pt-3 sm:px-6 sm:pt-4 pb-4">
      <div
        v-if="active && !loading && !locations.length"
        class="flex flex-1 items-center justify-center py-8 text-center text-gray-500"
      >
        No donations with location data found for the current filters.
      </div>
      <div
        v-show="active && (loading || locations.length)"
        class="relative min-h-0 flex-1 w-full overflow-hidden bg-white"
      >
        <div ref="mapContainer" class="absolute inset-0 h-full w-full bg-white" />
        <div
          v-if="loading"
          class="absolute inset-0 z-10 flex items-center justify-center bg-white/80"
        >
          <Spinner />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import Spinner from '@/components/Spinner'
import BaseButton from '@/components/ui/BaseButton.vue'
import { createDonationLocationsMap } from '@/utils/googleMaps'

export default {
  name: 'DonationMapSection',
  components: {
    Spinner,
    BaseButton,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    locations: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    canDrawExportArea: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['polygon-change'],
  setup(props, { emit }) {
    const mapContainer = ref(null)
    const mapShell = ref(null)
    const mapHostMounted = ref(false)
    const mapReady = ref(false)
    const isDrawing = ref(false)
    const draftPointCount = ref(0)
    const hasActivePolygon = ref(false)
    let mapHandle = null
    let drawingHandle = null

    const destroyMap = () => {
      drawingHandle?.destroy()
      drawingHandle = null
      mapHandle?.destroy()
      mapHandle = null
      mapReady.value = false
      isDrawing.value = false
      draftPointCount.value = 0
      hasActivePolygon.value = false
    }

    const clearDrawingTools = () => {
      drawingHandle?.destroy()
      drawingHandle = null
      isDrawing.value = false
      draftPointCount.value = 0
      hasActivePolygon.value = false
      emit('polygon-change', null)
    }

    const attachDrawingTools = async () => {
      if (!props.canDrawExportArea || !mapHandle || drawingHandle) {
        return Boolean(drawingHandle)
      }

      drawingHandle = await mapHandle.attachDrawing({
        onPolygonComplete: (coordinates) => {
          hasActivePolygon.value = coordinates.length >= 3
          emit('polygon-change', coordinates)
        },
        onPolygonClear: () => {
          hasActivePolygon.value = false
          emit('polygon-change', null)
        },
        onDrawingStateChange: (drawing) => {
          isDrawing.value = drawing
        },
        onDraftPointCountChange: (count) => {
          draftPointCount.value = count
        },
      })
      return Boolean(drawingHandle)
    }

    const ensureDrawingTools = async () => {
      if (!props.canDrawExportArea) {
        return false
      }
      if (drawingHandle) {
        return true
      }
      if (!mapHandle) {
        return false
      }

      try {
        return await attachDrawingTools()
      } catch (error) {
        console.error('Failed to attach drawing tools:', error)
        return false
      }
    }

    const syncMap = async () => {
      if (!mapHostMounted.value || !props.active) {
        return
      }

      if (!props.locations.length) {
        mapReady.value = false
        return
      }

      await nextTick()
      await new Promise((resolve) => requestAnimationFrame(resolve))
      if (!mapContainer.value) {
        return
      }

      if (!mapHandle) {
        try {
          mapHandle = await createDonationLocationsMap(mapContainer.value, props.locations)
          mapReady.value = true
          if (props.canDrawExportArea) {
            await attachDrawingTools()
          }
        } catch (error) {
          console.error('Failed to initialize donation map:', error)
          mapReady.value = false
        }
        return
      }

      if (props.loading) {
        return
      }

      await mapHandle.updateLocations(props.locations)
    }

    const refitMap = async () => {
      if (!mapHandle) {
        return
      }
      await nextTick()
      await new Promise((resolve) => requestAnimationFrame(resolve))
      mapHandle.refit()
    }

    const startDrawPolygon = async () => {
      const ready = await ensureDrawingTools()
      if (ready) {
        drawingHandle.startDrawing()
      }
    }

    const finishDrawPolygon = () => {
      drawingHandle?.finishDrawing()
    }

    const cancelDrawPolygon = () => {
      drawingHandle?.stopDrawing()
    }

    const clearDrawnPolygon = () => {
      drawingHandle?.clearPolygon()
    }

    watch(
      () => props.canDrawExportArea,
      (canDraw) => {
        if (!canDraw) {
          clearDrawingTools()
        }
      },
    )

    watch(
      () => props.active,
      (isActive) => {
        if (isActive) {
          mapHostMounted.value = true
        }
      },
    )

    watch(
      () => [props.active, props.loading, props.locations],
      async () => {
        await syncMap()
        if (props.active) {
          await refitMap()
        }
      },
      { deep: true, immediate: true },
    )

    onBeforeUnmount(() => {
      destroyMap()
    })

    return {
      mapContainer,
      mapShell,
      mapHostMounted,
      mapReady,
      isDrawing,
      draftPointCount,
      hasActivePolygon,
      startDrawPolygon,
      finishDrawPolygon,
      cancelDrawPolygon,
      clearDrawnPolygon,
    }
  },
}
</script>
